import ButtonWithLoader from "@/common/components/ui/loading/ButtonLoading";
import GenericTable from "@/common/components/ui/table/GenericTable";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { formatCurrency } from "@/common/helpers/mask.helpers";
import { verifyWalletId } from "@/modules/renters/helpers/verifyWalletId";
import { PaymentInfo } from "@/types/payment.type";
import { useCallback, useEffect, useState } from "react";

// Imports dos helpers
import { sortPaymentsByDueDate } from "../../../helpers/sortPayments.helper";
import {
  convertToNegativationPayments,
  calculatePaymentTotals,
  formatPaymentsForTable,
  PaymentsNegativation
} from "../../../helpers/paymentCalculations.helper";
import {
  togglePaymentSelection,
  areAllPaymentsSelected,
  areSomePaymentsSelected
} from "../../../helpers/paymentSelection.helper";
import {
  submitNegativation,
  prepareNegativationData,
  updatePayment
} from "../../../helpers/negativationApi.helper";

import { PaymentSelector } from "../../table/TableSelectedPayments";

type Props = {
  paymentsValid: PaymentInfo[];
  renterId: undefined | string;
}

export function FormNegativation({ paymentsValid, renterId }: Props) {

  const [selectedPayments, setSelectedPayments] = useState<PaymentsNegativation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [disablebuttonPayment, setDisablebuttonPayment] = useState(false);
  const [disablebuttonNegativation, setDisablebuttonNegativation] = useState(true);
  const [isLoadingUpdate, setIsLoadingUpdate] = useState(false);
  const { notifySuccess, notifyError } = useToastContext();

  // Converter e ordenar pagamentos (mais antigo primeiro)
  const sortedPayments = sortPaymentsByDueDate(paymentsValid);
  const payments = convertToNegativationPayments(sortedPayments);

  // Handler usando o helper
  const handlerPaymentsValid = useCallback((payment: PaymentsNegativation) => {
    setSelectedPayments((current) => togglePaymentSelection(current, payment));
  }, []);

  // Cálculos usando o helper
  const totals = calculatePaymentTotals(selectedPayments);


  useEffect(() => {
    if (selectedPayments.length === 1) {
      setDisablebuttonNegativation(false);
    } else {
      setDisablebuttonNegativation(true);
    }
  }, [selectedPayments]);

  // Verificações de seleção usando helpers
  const allSelected = areAllPaymentsSelected(selectedPayments, payments);
  const someSelected = areSomePaymentsSelected(selectedPayments, payments);

  const headers = {
    description: "Descrição",
    value: "Valor",
    split: "Split",
    splitValue: "Valor do Split",
    splitPercentualValue: "Percentual do Split",
  };

  // Dados da tabela usando o helper
  const data = formatPaymentsForTable(selectedPayments);

  const totalRow = {
    description: "Total",
    value: `${formatCurrency(totals.totalValue)}`,
    split: "",
    splitValue: `${formatCurrency(totals.totalSplitValue)}`,
    splitPercentualValue: `${totals.splitPercentOnTotal.toFixed(4)}%`,
  };

  //verificar se o pagmento possui split
  // Prepara os dados para requisição usando o helper
  const walletId = verifyWalletId(selectedPayments as PaymentInfo[]);
  const dataToRequest = prepareNegativationData(selectedPayments, totalRow, walletId || '', renterId);

  // Callbacks para controle de estado
  const submitCallbacks = {
    onLoadingStart: () => setIsLoading(true),
    onLoadingEnd: () => setIsLoading(false),
    onSuccess: notifySuccess,
    onError: notifyError,
  };

  const updateCallbacks = {
    onLoadingStart: () => setIsLoadingUpdate(true),
    onLoadingEnd: () => setIsLoadingUpdate(false),
    onSuccess: notifySuccess,
    onError: notifyError,
    onUpdateSuccess: () => {
      setDisablebuttonPayment(true);
      setDisablebuttonNegativation(false);
    },
  };

  // Handlers usando os helpers
  const handleSubmit = async () => {
    await submitNegativation(dataToRequest, submitCallbacks);
  };

  const handleUpdatePayment = async () => {
    await updatePayment(dataToRequest, updateCallbacks);
  };

  return (
    <section id="form-restriction" className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold mb-2">Pagamentos Válidos para Restrição</h2>
      <div className="space-y-2">
        <div className="flex items-center mb-2">
          <input
            type="checkbox"
            id="select-all"
            checked={allSelected}
            ref={input => {
              if (input) {
                input.indeterminate = someSelected;
              }
            }}
            onChange={() => {
              if (allSelected) {
                setSelectedPayments([]);
              } else {
                setSelectedPayments(payments);
              }
            }}
            className="mr-2 accent-primary"
          />
          <label htmlFor="select-all" className="font-medium text-gray-800 cursor-pointer">
            Selecionar todos
          </label>
        </div>
        {/* Componente de seleção de pagamentos */}
        <PaymentSelector
          payments={payments}
          selectedPayments={selectedPayments}
          onPaymentToggle={handlerPaymentsValid}
        />
      </div>
      <h2 className="text-lg font-semibold mb-2">Resumo</h2>
      <GenericTable headers={headers} data={data.concat(totalRow)} />

      <div className="flex mt-4 justify-between gap-4">
        <ButtonWithLoader
          isLoading={isLoadingUpdate}
          onClick={handleUpdatePayment}
          disabled={disablebuttonPayment}
        >
          Atualizar Pagamento
        </ButtonWithLoader>

        <ButtonWithLoader
          isLoading={isLoading}
          onClick={handleSubmit}
          disabled={disablebuttonNegativation}
        >
          Confirmar Negativação
        </ButtonWithLoader>

      </div>

    </section>
  );
}